/**
 * 用户浏览时间轴 Tracking SDK — Vue2 PC 端
 *
 * 自动记录用户页面浏览轨迹（enter / heartbeat / leave）
 * 上报接口：POST http://api.ycj.cc/track/visit（ThinkPHP）
 *
 * 使用方式：
 *   import tracking from '@/utils/tracking/index'
 *   tracking.init(router)           // main.js 中初始化
 *   tracking.setNextReferrer({...}) // 跳转前设置来源
 */

// ThinkPHP 埋点接口地址
const TRACK_API = 'https://api.csycj.com/track/visit'
 //const TRACK_API = 'http://api.ycj.cc/track/visit'
// 失败队列存储 key
const FAIL_QUEUE_KEY = 'tracking_fail_queue'
const FAIL_QUEUE_MAX = 100

// Storage keys
const STORAGE_SESSION   = 'tracking_session_id'
const STORAGE_ANONYMOUS = 'tracking_anonymous_id'
const STORAGE_DEVICE    = 'tracking_device_id'

// 心跳间隔（毫秒）
const HEARTBEAT_INTERVAL = 30 * 1000

// 商品详情页路径关键词（匹配到即认为 object_type = Goods）
const GOODS_PAGE_PATHS = ['/goodsDetail', '/GoodsDetailPreview', '/preempt/details', '/ProductDetails']

// 路径 → 中文名称映射（兜底 router.meta.nameCN 缺失的子路由）
const PATH_NAME_MAP = {
  // ShopCart 子路由（父路由 meta.nameCN 不会继承到子路由）
  '/ShopCart':            '购物车',
  '/ShopCart/MyCart':     '购物车',
  '/ShopCart/CheckOder':  '确认订单',
  '/ShopCart/PayOder':    '支付订单',
  '/ShopCart/CompletePay':'支付完成',
  // home 子路由
  '/home':                '主页',
  '/home/myAddress':      '收货地址',
  '/home/addAddress':     '新增地址',
  '/home/myOrder':        '我的订单',
  '/home/myShoppingCart': '我的购物车',
  // SignUp 子路由
  '/SignUp':              '注册',
  '/SignUp/checkPhone':   '验证手机号',
  '/SignUp/inputInfo':    '填写信息',
  '/SignUp/signUpDone':   '注册完成',
  // survey
  '/survey/list':         '调研列表',
  '/survey/detail':       '调研详情',
  '/survey/success':      '调研提交成功',
  // 其他缺失 nameCN 的路由
  '/payDone':             '支付完成',
  '/merchant':            '商家',
  '/NoLogin':             '登录提示',
  '/IndexPopup':          '首页弹窗',
  '/Collect':             '收藏',
  '/NoCollect':           '取消收藏',
  '/RightFlex':           '右侧悬浮',
  '/BackTop':             '返回顶部',
}

class Tracking {
  constructor() {
    this._sessionId      = null
    this._anonymousId    = null
    this._deviceId       = null
    this._currentVisit   = null   // 当前页面访问状态
    this._lastVisitId    = null   // 上一次访问的 visit_id（加购标记用）
    this._heartbeatTimer = null
    this._referrerData   = null   // 待消费的来源信息（手动设置）
    this._autoReferrer   = null   // 自动提取的来源（路由守卫传入 from）
    this._initialized    = false
    this._router         = null
  }

  // ==================== 初始化 ====================

  /**
   * 初始化 SDK（在 main.js 中调用）
   * 1. 生成/恢复 ID
   * 2. 注册路由守卫（可跳过）
   * 3. 注册页面关闭监听
   * 4. 重试失败队列
   *
   * @param {Object} router - vue-router 实例
   * @param {Object} [options]
   * @param {boolean} [options.autoGuard=true] - 是否自动注册路由守卫
   *   设为 false 时需手动在 beforeEach/afterEach 中调用 leave()/enter()
   */
  init(router, options) {
    if (this._initialized) return
    this._router = router
    var opts = options || {}
    var autoGuard = opts.autoGuard !== false  // 默认 true

    // 初始化 ID
    this._sessionId   = this._loadOrGenerate(STORAGE_SESSION, 'session')
    this._anonymousId = this._loadOrGenerate(STORAGE_ANONYMOUS, 'local')
    this._deviceId    = this._loadOrGenerate(STORAGE_DEVICE, 'local')

    // 注册路由守卫（autoGuard=false 时跳过，由 main.js 手动调用）
    if (autoGuard) {
      this._setupRouterGuards()
    }

    // 注册页面关闭监听
    this._setupPageCloseHandlers()

    this._initialized = true
    console.log('[Tracking] PC SDK initialized, session=' + this._sessionId)

    // 启动时重试失败队列
    this.retryFailedEvents()
  }

  // ==================== ID 管理 ====================

  /** 获取 session_id（当前浏览器会话内不变，sessionStorage） */
  getSessionId() {
    if (!this._sessionId) {
      this._sessionId = this._loadOrGenerate(STORAGE_SESSION, 'session')
    }
    return this._sessionId
  }

  /** 获取 anonymous_id（长期保存，localStorage） */
  getAnonymousId() {
    if (!this._anonymousId) {
      this._anonymousId = this._loadOrGenerate(STORAGE_ANONYMOUS, 'local')
    }
    return this._anonymousId
  }

  /** 获取登录用户 ID */
  getUserId() {
    try {
      var Cookies = require('js-cookie')
      // 优先从 userInfo Cookie 中取 user_id
      var info = Cookies.get('userInfo')
      if (info) {
        var parsed = JSON.parse(info)
        if (parsed && parsed.user_id) return String(parsed.user_id)
      }
      // 兑底：独立 userId Cookie
      return Cookies.get('userId') || ''
    } catch (e) {}
    return ''
  }

  /** 获取设备 ID */
  _getDeviceId() {
    if (!this._deviceId) {
      this._deviceId = this._loadOrGenerate(STORAGE_DEVICE, 'local')
    }
    return this._deviceId
  }

  // ==================== 设备信息 ====================

  /**
   * 获取 client_type + platform
   * PC 端固定返回 { client_type: 'pc', platform: 'pc' }
   */
  getClientInfo() {
    return { client_type: 'pc', platform: 'pc' }
  }

  /** 获取 user_agent 字符串 */
  _getUserAgent() {
    return navigator.userAgent || ''
  }

  // ==================== 页面信息 ====================

  /**
   * 从 vue-router route 对象获取页面信息
   * @param {Object} route - vue-router 的 route 对象
   * @returns {Object}
   */
  getPageInfo(route) {
    if (!route) {
      route = this._router ? this._router.currentRoute : null
    }
    if (!route) {
      return {
        object_type: 'Page', object_id: '', object_name: '',
        goods_id: '', path: '', url: '', route_name: ''
      }
    }

    const path    = route.path || ''
    const query   = route.query || {}
    const meta    = route.meta || {}

    // 判断是否为商品详情页
    const isGoodsPage = GOODS_PAGE_PATHS.some(p => path.indexOf(p) !== -1)

    let object_type = 'Page'
    let object_id   = path
    let object_name = ''
    let goods_id    = ''

    // object_name 优先级：meta.title → meta.nameCN → 映射表 → document.title → path
    if (isGoodsPage && query.id) {
      object_type = 'Goods'
      object_id   = String(query.id)
      goods_id    = String(query.id)
      object_name = meta.nameCN || PATH_NAME_MAP[path] || document.title || ('商品' + query.id)
    } else {
      object_name = meta.title || meta.nameCN || PATH_NAME_MAP[path] || document.title || path
    }

    // /GoodsList?show_id=xxx 为动态专区页，覆盖默认的"商品列表"名称
    // 后续由组件调用 updatePageInfo() 异步更新为真实专区名称（如"普药专区"）
    if (path === '/GoodsList' && query.show_id) {
      object_name = '专区'
    }

    // 构建完整 URL
    const queryStr = Object.keys(query).length > 0
      ? '?' + Object.entries(query).map(([k, v]) => k + '=' + v).join('&')
      : ''

    return {
      object_type,
      object_id,
      object_name,
      goods_id,
      path,
      url: path + queryStr,
      route_name: meta.nameCN || PATH_NAME_MAP[path] || route.name || path,
    }
  }

  // ==================== Referrer 来源 ====================

  /**
   * 设置下一跳来源信息（在跳转前调用）
   *
   * @param {Object} referrer
   * @param {string} referrer.referrer_type  - 来源类型，如 'Page'
   * @param {string} referrer.referrer_id    - 来源路由 path
   * @param {string} referrer.referrer_name  - 来源页面名称
   * @param {string} referrer.referrer_url   - 来源完整 URL
   * @param {string} referrer.source_module  - 来源模块
   * @param {string} referrer.source_action  - 来源动作
   */
  setNextReferrer(referrer) {
    this._referrerData = {
      referrer_type:  referrer.referrer_type || '',
      referrer_id:    referrer.referrer_id || '',
      referrer_name:  referrer.referrer_name || '',
      referrer_url:   referrer.referrer_url || '',
      source_module:  referrer.source_module || '',
      source_action:  referrer.source_action || '',
    }
  }

  /**
   * 从 vue-router 的 from 路由对象自动提取来源信息
   * 在 afterEach 路由守卫中，from 就是用户离开的页面
   * @param {Object} from - vue-router 的 from 路由对象
   * @returns {Object} referrer 字段集
   */
  _extractReferrerFromRoute(from) {
    if (!from || !from.path) {
      return null
    }
    const meta = from.meta || {}
    const query = from.query || {}
    const referrerName = meta.nameCN || meta.title || from.name || from.path
    const isGoods = GOODS_PAGE_PATHS.some(function(p) { return from.path.indexOf(p) !== -1 })
    var queryStr = Object.keys(query).length > 0
      ? '?' + Object.entries(query).map(function(e) { return e[0] + '=' + e[1] }).join('&')
      : ''
    return {
      referrer_type:  isGoods ? 'Goods' : 'Page',
      referrer_id:    from.path,
      referrer_name:  referrerName,
      referrer_url:   from.path + queryStr,
      source_module:  '',
      source_action:  '',
    }
  }

  /**
   * 消费来源信息（enter 时调用，用完即清）
   * 优先使用 setNextReferrer() 手动设置的来源；
   * 若未手动设置，则使用 _autoReferrer（从 from 路由自动提取的）
   * 若两者都没有，返回"直接访问"兜底
   */
  consumeReferrer() {
    // 1. 优先：手动设置的来源（业务代码显式调用 setNextReferrer）
    if (this._referrerData) {
      var ref = this._referrerData
      this._referrerData = null
      this._autoReferrer = null
      return Object.assign({}, ref)
    }
    // 2. 自动提取的来源（路由守卫中传入的 from）
    if (this._autoReferrer) {
      var autoRef = this._autoReferrer
      this._autoReferrer = null
      return Object.assign({}, autoRef)
    }
    // 3. 兜底：直接访问
    return {
      referrer_type: 'Direct', referrer_id: '', referrer_name: '直接访问',
      referrer_url: '', source_module: '', source_action: '',
    }
  }

  // ==================== 生命周期 ====================

  /**
   * 页面进入（在 afterEach 中由路由守卫调用）
   * @param {Object} to - vue-router 的 to 对象
   * @param {Object} [from] - vue-router 的 from 对象（来源路由，用于自动提取来源信息）
   */
  enter(to, from) {
    if (!this._initialized) return

    // 如果已有活跃访问，先 leave
    if (this._currentVisit && !this._currentVisit._hasLeft) {
      this._doLeave()
    }

    // 自动从 from 路由提取来源信息（优先级低于手动 setNextReferrer）
    if (from) {
      this._autoReferrer = this._extractReferrerFromRoute(from)
    }

    const pageInfo  = this.getPageInfo(to)
    const referrer  = this.consumeReferrer()
    const enterTime = this._now()
    const visitId   = this._generateId()

    this._currentVisit = {
      visit_id:      visitId,
      enter_time:    enterTime,
      enter_ms:      Date.now(),
      pageInfo:      pageInfo,
      referrer:      referrer,
      _path:         pageInfo.path,
      _hasLeft:      false,
    }

    // 缓存当前 visit_id，供页面离开后的加购标记使用
    this._lastVisitId = visitId

    this._report('enter')
    this._startHeartbeat()
  }

  /**
   * 页面离开（在 beforeEach 中由路由守卫调用）
   */
  leave() {
    this._doLeave()
  }

  /** 内部 leave 实现 */
  _doLeave() {
    if (!this._initialized || !this._currentVisit) return
    if (this._currentVisit._hasLeft) return

    this._currentVisit._hasLeft = true
    this._stopHeartbeat()
    this._updateDuration()
    this._report('leave')
    this._currentVisit = null
  }

  /** 心跳（每 30 秒一次） */
  _heartbeat() {
    if (!this._initialized || !this._currentVisit || this._currentVisit._hasLeft) return

    // 页面隐藏时不发心跳
    if (document.hidden) return

    this._updateDuration()
    this._report('heartbeat')
  }

  // ==================== 路由守卫 ====================

  _setupRouterGuards() {
    if (!this._router) return

    // beforeEach：处理上一个页面 leave
    this._router.beforeEach((to, from, next) => {
      try {
        if (this._currentVisit && !this._currentVisit._hasLeft) {
          this._doLeave()
        }
      } catch (e) {
        console.warn('[Tracking] beforeEach error:', e)
      }
      next()
    })

    // afterEach：处理新页面 enter（同时传 from 用于来源页采集）
    this._router.afterEach((to, from) => {
      try {
        this.enter(to, from)
      } catch (e) {
        console.warn('[Tracking] afterEach error:', e)
      }
    })
  }

  // ==================== 页面关闭监听 ====================

  _setupPageCloseHandlers() {
    // 1. pagehide（最可靠）
    window.addEventListener('pagehide', () => {
      this._sendBeaconLeave()
    })

    // 2. beforeunload
    window.addEventListener('beforeunload', () => {
      this._sendBeaconLeave()
    })

    // 3. visibilitychange
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        // 页面隐藏时：停止心跳，上报一次 heartbeat
        if (this._currentVisit && !this._currentVisit._hasLeft) {
          this._updateDuration()
          this._sendBeaconLeave('heartbeat')
          this._stopHeartbeat()
        }
      } else {
        // 页面恢复可见：恢复心跳
        if (this._currentVisit && !this._currentVisit._hasLeft) {
          this._startHeartbeat()
        }
      }
    })
  }

  /**
   * 页面关闭/隐藏时通过 sendBeacon 上报
   * 优先 sendBeacon → fetch keepalive → 放弃（不阻塞关闭）
   */
  _sendBeaconLeave(eventType) {
    if (!this._currentVisit || this._currentVisit._hasLeft) return
    if (!eventType) eventType = 'leave'

    this._currentVisit._hasLeft = true
    this._updateDuration()

    const v          = this._currentVisit
    const clientInfo = this.getClientInfo()
    const pg         = v.pageInfo
    const ref        = v.referrer

    const payload = {
      event_id:       this._generateId(),
      event_type:     eventType,
      visit_id:       v.visit_id,
      user_id:        this.getUserId(),
      anonymous_id:   this.getAnonymousId(),
      session_id:     this.getSessionId(),
      client_type:    clientInfo.client_type,
      platform:       clientInfo.platform,
      object_type:    pg.object_type,
      object_id:      pg.object_id,
      object_name:    pg.object_name,
      goods_id:       pg.goods_id,
      enter_time:     v.enter_time,
      event_time:     this._now(),
      duration_seconds: v.duration_seconds || 0,
      referrer_type:  ref.referrer_type || '',
      referrer_id:    ref.referrer_id || '',
      referrer_name:  ref.referrer_name || '',
      referrer_url:   ref.referrer_url || '',
      source_module:  ref.source_module || '',
      source_action:  ref.source_action || '',
      url:            pg.url || '',
      path:           pg.path || '',
      route_name:     pg.route_name || '',
      device_id:      this._getDeviceId(),
      user_agent:     this._getUserAgent(),
    }

    const payloadStr = JSON.stringify(payload)

    // 优先 sendBeacon
    if (navigator.sendBeacon) {
      const blob = new Blob([payloadStr], { type: 'application/json' })
      const sent = navigator.sendBeacon(TRACK_API, blob)
      if (sent) {
        console.log('[Tracking] sendBeacon ' + eventType + ' ✓')
        return
      }
    }

    // 降级 fetch keepalive
    try {
      fetch(TRACK_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payloadStr,
        keepalive: true,
      })
      console.log('[Tracking] fetch keepalive ' + eventType + ' ✓')
    } catch (e) {
      // 最终降级：写入失败队列
      console.warn('[Tracking] sendBeacon/fetch 均失败，写入队列')
      this._addToFailQueue(payload)
    }

    this._stopHeartbeat()
    if (eventType === 'leave') {
      this._currentVisit = null
    }
  }

  // ==================== 上报 ====================

  /**
   * 内部上报方法
   * @param {string} eventType - enter / heartbeat / leave
   */
  _report(eventType) {
    if (!this._currentVisit) return

    const v          = this._currentVisit
    const clientInfo = this.getClientInfo()
    const pg         = v.pageInfo
    const ref        = v.referrer

    const payload = {
      event_id:       this._generateId(),
      event_type:     eventType,
      visit_id:       v.visit_id,
      user_id:        this.getUserId(),
      anonymous_id:   this.getAnonymousId(),
      session_id:     this.getSessionId(),
      client_type:    clientInfo.client_type,
      platform:       clientInfo.platform,
      object_type:    pg.object_type,
      object_id:      pg.object_id,
      object_name:    pg.object_name,
      goods_id:       pg.goods_id,
      enter_time:     v.enter_time,
      event_time:     this._now(),
      duration_seconds: v.duration_seconds || 0,
      referrer_type:  ref.referrer_type || '',
      referrer_id:    ref.referrer_id || '',
      referrer_name:  ref.referrer_name || '',
      referrer_url:   ref.referrer_url || '',
      source_module:  ref.source_module || '',
      source_action:  ref.source_action || '',
      url:            pg.url || '',
      path:           pg.path || '',
      route_name:     pg.route_name || '',
      device_id:      this._getDeviceId(),
      user_agent:     this._getUserAgent(),
    }

    // 使用 fetch（不依赖 axios，避免被业务拦截器影响）
    fetch(TRACK_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }).then(res => {
      if (res.ok) {
        console.log('[Tracking] ' + eventType + ' ✓')
        this.retryFailedEvents()
      } else {
        console.warn('[Tracking] ' + eventType + ' ✗ status=' + res.status)
        this._addToFailQueue(payload)
      }
    }).catch(() => {
      console.warn('[Tracking] ' + eventType + ' ✗ network error')
      this._addToFailQueue(payload)
    })
  }

  // ==================== 失败重试 ====================

  /** 将失败事件加入 localStorage 队列 */
  _addToFailQueue(payload) {
    try {
      let queue = JSON.parse(localStorage.getItem(FAIL_QUEUE_KEY) || '[]')
      queue.push(payload)
      if (queue.length > FAIL_QUEUE_MAX) {
        queue = queue.slice(queue.length - FAIL_QUEUE_MAX)
      }
      localStorage.setItem(FAIL_QUEUE_KEY, JSON.stringify(queue))
    } catch (e) {
      console.warn('[Tracking] 写入失败队列异常', e)
    }
  }

  /**
   * 重试失败事件
   * 每条最多重试 3 次
   */
  retryFailedEvents() {
    try {
      const queue = JSON.parse(localStorage.getItem(FAIL_QUEUE_KEY) || '[]')
      if (queue.length === 0) return

      localStorage.setItem(FAIL_QUEUE_KEY, '[]')

      queue.forEach(item => {
        item._retryCount = (item._retryCount || 0) + 1
        if (item._retryCount > 3) return

        fetch(TRACK_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item),
        }).then(res => {
          if (res.ok) {
            console.log('[Tracking] 重试成功: ' + item.event_type)
          } else {
            this._addToFailQueue(item)
          }
        }).catch(() => {
          this._addToFailQueue(item)
        })
      })
    } catch (e) {
      console.warn('[Tracking] 重试失败队列异常', e)
    }
  }

  // ==================== 心跳控制 ====================

  _startHeartbeat() {
    this._stopHeartbeat()
    this._heartbeatTimer = setInterval(() => {
      this._heartbeat()
    }, HEARTBEAT_INTERVAL)
  }

  _stopHeartbeat() {
    if (this._heartbeatTimer) {
      clearInterval(this._heartbeatTimer)
      this._heartbeatTimer = null
    }
  }

  // ==================== 工具方法 ====================

  /** 生成 UUID v4 */
  _generateId() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = Math.random() * 16 | 0
      var v = c === 'x' ? r : (r & 0x3 | 0x8)
      return v.toString(16)
    })
  }

  /** 当前时间格式化 'YYYY-MM-DD HH:mm:ss' */
  _now() {
    var d = new Date()
    var pad = function (n) { return String(n).padStart(2, '0') }
    return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()) +
      ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes()) + ':' + pad(d.getSeconds())
  }

  /** 从 storage 加载或生成 ID */
  _loadOrGenerate(storageKey, type) {
    var val = ''
    try {
      val = type === 'session'
        ? window.sessionStorage.getItem(storageKey)
        : window.localStorage.getItem(storageKey)
    } catch (e) {}
    if (!val) {
      val = this._generateId()
      try {
        if (type === 'session') {
          window.sessionStorage.setItem(storageKey, val)
        } else {
          window.localStorage.setItem(storageKey, val)
        }
      } catch (e) {}
    }
    return val
  }

  /** 更新当前访问的停留时长 */
  _updateDuration() {
    if (!this._currentVisit) return
    var elapsed = Math.floor((Date.now() - this._currentVisit.enter_ms) / 1000)
    this._currentVisit.duration_seconds = Math.max(0, elapsed)
  }

  // ==================== 供页面手动调用 ====================

  /**
   * 标记当前页面访问期间发生了加购行为
   * 向后端 /track/addCart 发送请求，将 has_add_cart 置为 1
   *
   * 优先使用当前访问的 visit_id；如果页面已离开（_currentVisit 为 null），
   * 则回退到 _lastVisitId（缓存的上一次访问 ID），确保加购标记不丢失。
   *
   * @param {string|number} [goodsId] - 加购的商品ID
   */
  markAddCart(goodsId) {
    var visitId = null
    if (this._currentVisit) {
      visitId = this._currentVisit.visit_id
    } else if (this._lastVisitId) {
      visitId = this._lastVisitId
      console.log('[Tracking] markAddCart: 使用缓存的 visit_id=' + visitId)
    }

    if (!visitId) {
      console.warn('[Tracking] markAddCart: 无可用的 visit_id，跳过')
      return
    }

    var url = TRACK_API.replace('/visit', '/addCart')
    console.log('[Tracking] markAddCart → visit_id=' + visitId + ', goods_id=' + (goodsId || ''))
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ visit_id: visitId, goods_id: String(goodsId || '') }),
    }).then(function(res) {
      if (res.ok) {
        console.log('[Tracking] markAddCart ✓')
      } else {
        console.warn('[Tracking] markAddCart ✗ status=' + res.status)
      }
    }).catch(function(e) {
      console.warn('[Tracking] markAddCart ✗ network error', e)
    })
  }

  /**
   * 更新当前页面的商品名称
   * 适用于商品详情页异步加载完成后调用
   */
  updateGoodsName(name) {
    if (this._currentVisit && name) {
      this._currentVisit.pageInfo.object_name = name
    }
  }

  /**
   * 更新当前页面的页面信息
   */
  updatePageInfo(info) {
    if (this._currentVisit && info) {
      if (info.object_name) this._currentVisit.pageInfo.object_name = info.object_name
      if (info.route_name)  this._currentVisit.pageInfo.route_name  = info.route_name
    }
  }

  // ==================== 身份合并 ====================

  /**
   * 登录成功后调用，将登录前的匿名记录补充 user_id
   * 后端会将最近 7 天内同 anonymous_id 且 user_id 为空的记录回填 user_id
   */
  identityMerge() {
    var userId = this.getUserId()
    var anonymousId = this.getAnonymousId()
    if (!userId || !anonymousId) return

    fetch(TRACK_API.replace('/visit', '/bindAnonymous'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, anonymous_id: anonymousId }),
    }).then(function (res) {
      if (res.ok) {
        console.log('[Tracking] identityMerge ✓')
      }
    }).catch(function () {})
  }
}

// 导出单例
var tracking = new Tracking()
export default tracking

// 全局快捷调用，供业务组件直接 window.__tracking_markAddCart(goodsId)
if (typeof window !== 'undefined') {
  window.__tracking_markAddCart = function(goodsId) { tracking.markAddCart(goodsId) }
}



// WEBPACK FOOTER //
// ./src/utils/tracking/index.js